import { useState } from 'react';
import { Loader2, Search, Play, BarChart3, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function BucketBadge({ bucket }) {
  if (bucket === 'paid') return <Badge variant="outline" className="text-green-600 border-green-400">paid</Badge>;
  if (bucket === 'partially_paid') return <Badge variant="outline" className="text-amber-600 border-amber-400">partially_paid</Badge>;
  if (bucket === null || bucket === undefined) return <Badge variant="outline" className="text-gray-500 border-gray-400">none (fallthrough)</Badge>;
  return <Badge variant="outline">{bucket}</Badge>;
}

export function Tier0DiagnosticsPanel() {
  // Populations
  const [popLoading, setPopLoading] = useState(false);
  const [popData, setPopData] = useState(null);

  // Inspector
  const [inspectId, setInspectId] = useState('');
  const [inspectLoading, setInspectLoading] = useState(false);
  const [inspectData, setInspectData] = useState(null);

  // Run-now
  const [syncRunning, setSyncRunning] = useState(false);
  const [reconRunning, setReconRunning] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  const loadPopulations = async () => {
    setPopLoading(true);
    try {
      const { data } = await diagnosticsAPI.tier0Populations();
      setPopData(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPopLoading(false);
    }
  };

  const inspectBooking = async () => {
    if (!inspectId.trim()) return;
    setInspectLoading(true);
    setInspectData(null);
    try {
      const { data } = await diagnosticsAPI.tier0Inspect(inspectId.trim());
      setInspectData(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setInspectLoading(false);
    }
  };

  const runSync = async () => {
    setSyncRunning(true);
    setConfirmAction(null);
    try {
      const { data } = await diagnosticsAPI.pennylaneSyncRunNow();
      const log = data.sync_log || {};
      toast.success(`Sync complete: ${log.invoices_synced || 0} invoices, ${log.expenses_synced || 0} expenses`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSyncRunning(false);
    }
  };

  const runReconciliation = async () => {
    setReconRunning(true);
    setConfirmAction(null);
    try {
      const { data } = await diagnosticsAPI.pennylaneReconciliationRunNow();
      toast.success(`Reconciliation complete: ${data.status}`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setReconRunning(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-purple-500" />
          Tier 0 Pennylane Diagnostics
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Invoice payment-bucket populations, per-booking inspector, and manual sync/reconciliation triggers.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">

        {/* Population counts */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-medium">Population Counts</h4>
            <Button variant="outline" size="sm" onClick={loadPopulations} disabled={popLoading}>
              {popLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              <span className="ml-1">Load</span>
            </Button>
          </div>
          {popData && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="border rounded px-3 py-2 bg-muted/30">
                <div className="text-xs text-muted-foreground">Total Invoices</div>
                <div className="text-lg font-mono">{popData.total_invoices}</div>
              </div>
              <div className="border rounded px-3 py-2 bg-muted/30">
                <div className="text-xs text-muted-foreground">Gated (active)</div>
                <div className="text-lg font-mono">{popData.gated_invoices}</div>
              </div>
              <div className="border rounded px-3 py-2 bg-muted/30">
                <div className="text-xs text-muted-foreground">Excluded (review gate)</div>
                <div className="text-lg font-mono">{popData.excluded_by_review_gate}</div>
              </div>
              <div className="border rounded px-3 py-2 bg-muted/30">
                <div className="text-xs text-muted-foreground">Horizon</div>
                <div className="text-lg font-mono">{popData.reconciliation_horizon_days}d</div>
              </div>
              {popData.bucket_counts && (
                <>
                  <div className="border rounded px-3 py-2 bg-green-50 dark:bg-green-950/30">
                    <div className="text-xs text-muted-foreground">Paid</div>
                    <div className="text-lg font-mono text-green-700 dark:text-green-400">{popData.bucket_counts.paid}</div>
                  </div>
                  <div className="border rounded px-3 py-2 bg-amber-50 dark:bg-amber-950/30">
                    <div className="text-xs text-muted-foreground">Partially Paid</div>
                    <div className="text-lg font-mono text-amber-700 dark:text-amber-400">{popData.bucket_counts.partially_paid}</div>
                  </div>
                  <div className="border rounded px-3 py-2 bg-blue-50 dark:bg-blue-950/30">
                    <div className="text-xs text-muted-foreground">Awaiting Recon</div>
                    <div className="text-lg font-mono text-blue-700 dark:text-blue-400">{popData.bucket_counts.awaiting_pl_reconciliation}</div>
                  </div>
                  <div className="border rounded px-3 py-2 bg-red-50 dark:bg-red-950/30">
                    <div className="text-xs text-muted-foreground">Unpaid</div>
                    <div className="text-lg font-mono text-red-700 dark:text-red-400">{popData.bucket_counts.unpaid}</div>
                  </div>
                </>
              )}
            </div>
          )}
          {popData?.review_status_breakdown && (
            <div className="text-xs text-muted-foreground">
              Review status: {Object.entries(popData.review_status_breakdown).map(([k, v]) => `${k}: ${v}`).join(', ')}
            </div>
          )}
        </div>

        {/* Per-booking inspector */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium">Per-Booking Inspector</h4>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Booking ID (uplisting/smoobu/hostaway)"
              value={inspectId}
              onChange={(e) => setInspectId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && inspectBooking()}
              className="flex h-9 w-full max-w-xs rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
            />
            <Button variant="outline" size="sm" onClick={inspectBooking} disabled={inspectLoading || !inspectId.trim()}>
              {inspectLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Search className="h-3 w-3" />}
              <span className="ml-1">Inspect</span>
            </Button>
          </div>
          {inspectData && (
            <div className="border rounded px-4 py-3 bg-muted/30 space-y-2 text-sm">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-mono">{inspectData.booking_id}</span>
                <Badge variant="outline">{inspectData.platform}</Badge>
                <Badge variant="outline">{inspectData.channel}</Badge>
                <span className="text-muted-foreground">{inspectData.check_in} to {inspectData.check_out}</span>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-muted-foreground">Invoice:</span>
                {inspectData.invoice.found ? (
                  <>
                    <Badge variant="outline" className="text-green-600 border-green-400">found</Badge>
                    <span>status: {inspectData.invoice.payment_status}</span>
                    <span>pennylane_paid: {String(inspectData.invoice.pennylane_paid)}</span>
                    <span>review: {inspectData.invoice.review_status}</span>
                    <span>amount: {inspectData.invoice.amount}</span>
                  </>
                ) : (
                  <Badge variant="outline" className="text-gray-500 border-gray-400">not found</Badge>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-muted-foreground">Tier-0 bucket:</span>
                <BucketBadge bucket={inspectData.tier0_bucket} />
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-muted-foreground">Payment state:</span>
                <Badge variant="outline">{inspectData.payment_state.state}</Badge>
                <span>expected: {inspectData.payment_state.expected}</span>
                <span>received: {inspectData.payment_state.received}</span>
                {inspectData.payment_state.shortfall != null && (
                  <span className="text-red-600">shortfall: {inspectData.payment_state.shortfall}</span>
                )}
                <span>tier: {inspectData.payment_state.match_tier}</span>
              </div>
            </div>
          )}
        </div>

        {/* Run-now buttons */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium">Manual Triggers</h4>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              disabled={syncRunning || reconRunning}
              onClick={() => setConfirmAction('sync')}
            >
              {syncRunning ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Play className="h-3 w-3 mr-1" />}
              Pennylane Sync
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={syncRunning || reconRunning}
              onClick={() => setConfirmAction('reconciliation')}
            >
              {reconRunning ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Play className="h-3 w-3 mr-1" />}
              Pennylane Reconciliation
            </Button>
          </div>
        </div>

        {/* Confirm dialog */}
        <Dialog open={!!confirmAction} onOpenChange={(open) => { if (!open) setConfirmAction(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {confirmAction === 'sync' ? 'Confirm Pennylane Sync' : 'Confirm Pennylane Reconciliation'}
              </DialogTitle>
              <DialogDescription>
                {confirmAction === 'sync'
                  ? 'This will run the full Pennylane invoice sync (same as the scheduled job). Idempotent: existing invoices are upserted.'
                  : 'This will run Pennylane reconciliation: mark-as-paid for matched invoices and retry bank transaction matching.'}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmAction(null)}>Cancel</Button>
              <Button onClick={confirmAction === 'sync' ? runSync : runReconciliation}>
                Run Now
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </CardContent>
    </Card>
  );
}
